import React, {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {FormattedMessage} from "react-intl";
import queryString from "query-string";
import Head from "next/head";
import dynamic from 'next/dynamic'
import {domainUrl} from "../../helper";
import {MetaWrapper} from "../MetaWrapper";
import {IBlogDataPost, IBlogPageCategoryProps} from "../../types/pages";
import {IState} from "../../types/state";
import {useRouter} from "next/router";
import cls from "./blog.module.scss"

const BlockLoader = dynamic(() => import('../blocks/BlockLoader'))
const PageHeader = dynamic(() => import('../shared/PageHeader'))
const Pagination = dynamic(() => import('../shared/Pagination'))
const PostCard = dynamic(() => import('../shared/PostCard'))

function buildQuery(options: { page: number }) {
    const params = {page: 0};
    if (options.page !== 1) {
        params.page = options.page;
    }
    return queryString.stringify(params, {encode: false});
}

const BlogPageCategory = (props: IBlogPageCategoryProps) => {
    const router = useRouter();
    const selectedData = useSelector((state: IState) => state.locale.code);
    const [page, setPage] = useState<number>(props.blog.current_page);
    // const [blogs, setBlogs] = useState<[]>(props.blog.data);
    const [total, _] = useState<number>(props.blog.total);

    const prevLocaleCodeRef: { current: string | undefined } = useRef();
    const prevPageRef: { current: number | undefined } = useRef();

    useEffect(() => {
        prevPageRef.current = page;
        prevLocaleCodeRef.current = selectedData;
    }, []);

    useEffect(() => {
        if (page > 1) {
            const query = buildQuery({page});
            const location = `${window.location.pathname}${query ? "?" : ""}${query}`;
            window.history.replaceState(null, "", location);
        } else {
            const location = `${window.location.pathname}`;
            window.history.replaceState(null, "", location);
        }
    }, [page]);

    const handlePageChange = (page: number): void => {
        setPage(page);
    };

    const {layout, dbName, domain} = props;

    const breadcrumb = [
        {title: <FormattedMessage id="home" defaultMessage="Home"/>, url: `https://${dbName}/`},
        {title: <FormattedMessage id="blog" defaultMessage="Blog"/>, url: "/blog"},
    ];

    if (!props.blog.data) {
        return <BlockLoader/>;
    }

    const schemaBlog: {
        "@context": string,
        "@type": string,
        name: string,
        url: string,
        offers: [{
            id: number,
            image: string,
            name: string,
            description: string,
        }],
    } = {
        "@context": `https://schema.org/`,
        "@type": "Blog",
        name: "Page blogs",
        url: domainUrl(dbName + router.asPath),
        offers: [{
            id: 0,
            image: '',
            name: '',
            description: '',
        }],
    };

    const logoPath = `/stores-logo.svg`;

    const postsList = props.blog.data.map((post: IBlogDataPost) => {
        const postLayout: {
            classic: string,
            grid: string,
            list: string,
        } = {
            classic: "grid-lg",
            grid: "grid-nl",
            list: "list-nl",
        }
        // }[layout];

        schemaBlog.offers.push({
            id: post.id,
            image: `https://${props.domain}/storage/${props.dbName}/${logoPath}`,
            name: post.blog_title,
            description: post.html_content,
        });

        return (
            <div key={post.id} className={cls["posts-list__item"]}>
                <PostCard post={post} dbName={dbName} layout={postLayout} domain={domain}/>
            </div>
        );
    });

    return (
        <>
            <Head>
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{__html: JSON.stringify(schemaBlog)}}
                />
            </Head>
            <PageHeader header="Latest News" breadcrumb={breadcrumb}/>
            <div className="container">
                <div className="row">
                    <div>
                        <h1 className={cls["blog-page-title"]}>
                            <FormattedMessage id="blog" defaultMessage="Blog"/>
                        </h1>
                        {postsList.length ? (
                            <div className="block">
                                <div
                                    className={cls[`posts-list--layout--${layout}`]}
                                >
                                    <div className={cls["posts-list__body"]}>{postsList}</div>
                                </div>
                                <div className={cls["posts-view__pagination"]}>
                                    {postsList && postsList.length < 20 ? (
                                        <></>
                                    ) : (
                                        <Pagination
                                            current={+page}
                                            siblings={2}
                                            total={total}
                                            onPageChange={handlePageChange}
                                        />
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className={cls["products-view__empty"]}>
                                <div className={cls["products-view__empty-title"]}>
                                    <FormattedMessage
                                        id="sorry_nothing_found_for"
                                        defaultMessage="Sorry Nothing Was Found"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default BlogPageCategory;
